import type { ReactNode } from "react";
import type { VisibleColumn } from "./use-comparison-columns";

type Row = {
  id: string;
  label: ReactNode;
};

export type Section = {
  id: string;
  rows: Row[];
  title: ReactNode;
  icon?: ReactNode;
};

type ComparisonBodyProperties = {
  section: Section;
  showLabelColumn: boolean;
  visibleColumns: VisibleColumn[];
};

export const ComparisonBody = ({
  section,
  showLabelColumn,
  visibleColumns,
}: ComparisonBodyProperties) => (
  <tbody className="bg-background-surface-container">
    <tr>
      <th
        className="
          bg-surface-container-variant-lowest p-4 text-start text-title-m
          font-semibold text-foreground-primary-variant
        "
        colSpan={visibleColumns.length + (showLabelColumn ? 1 : 0)}
        scope="colgroup"
      >
        {/* Icon is always decorative here — the section title alone conveys
            the meaning, so we hide it from assistive tech regardless of
            what the consumer passes in. */}
        {section.icon && <span aria-hidden="true">{section.icon}</span>}
        {section.title}
      </th>
    </tr>

    {section.rows.map((row) => (
      <tr key={row.id}>
        {showLabelColumn && (
          <th
            className="
              border border-border-default p-4 text-start text-subtitle-m
              font-extrabold text-foreground-primary-variant
            "
            scope="row"
          >
            {row.label}
          </th>
        )}

        {visibleColumns.map(({ columnKey, item }) => (
          <td
            className="
              border border-border-default p-4 text-subtitle-m font-semibold
              text-foreground-primary-variant
            "
            key={columnKey}
          >
            {!showLabelColumn && (
              <div
                className="
                  mb-2 text-subtitle-m font-semibold
                  text-foreground-primary-variant
                "
              >
                {row.label}
              </div>
            )}

            {item.sections[section.id]?.[row.id] ?? (
              <div className="text-foreground-disabled">
                <span aria-hidden>&mdash;</span>
                <span className="sr-only">Not available</span>
              </div>
            )}
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);
